import * as jwt_decode from 'jwt-decode';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

import { UserModel, UserType } from '../data/model/auth-model';

interface JwtPayload {
  exp: number;
}

interface AuthContextType {
  user: UserModel | null;
  token: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isMember: boolean;
  userId: string | null;
  userName: string;
  userEmail: string | null;
  login: (user: UserModel, token: string) => void;
  logout: () => void;
  isTokenValid: (token: string | null) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserModel | null>(null);
  const [token, setToken] = useState<string | null>(null);

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');

      if (storedUser && storedToken) {
        try {
          const parsedUser: UserModel = JSON.parse(storedUser);
          setUser(parsedUser);
          setToken(storedToken);
        } catch (error) {
          console.error('Error parsing stored user data:', error);
          localStorage.removeItem('user');
          localStorage.removeItem('token');
        }
      }
    }
  }, []);

  const login = (userData: UserModel, userToken: string) => {
    setUser(userData);
    setToken(userToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', userToken);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  const isTokenValid = (token: string | null): boolean => {
    if (!token) return false;
    try {
      const decoded: JwtPayload = jwt_decode.jwtDecode<JwtPayload>(token);
      const now = Date.now() / 1000;
      return decoded.exp > now;
    } catch {
      return false;
    }
  };

  const userType = user?.type || null;
  const isAuthenticated = Boolean(token && user && userType);
  const isAdmin = userType === UserType.ADMIN;
  const isMember = userType === UserType.MEMBER;
  const userId = user?.id || null;
  const userName =
    user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : '';
  const userEmail = user?.email || null;

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated,
    isAdmin,
    isMember,
    userId,
    userName,
    userEmail,
    login,
    logout,
    isTokenValid,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
